import moment from 'moment';

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * Normaliza una fecha de GIS a su día 'YYYY-MM-DD'.
 * GIS v2 puede devolver solo el día ('2026-09-15') o medianoche UTC
 * ('2026-09-15T00:00:00.000Z'); en ambos casos el día del dato es el prefijo.
 * No se usa new Date()/toISOString() porque mezclan UTC y hora local y
 * desplazan un día las fechas en zonas con offset (EDSER-2138).
 * @param {String} date
 * @return {String} Día en formato 'YYYY-MM-DD'
 */
export function toDayKey(date) {
  return String(date).slice(0, 10);
}

/**
 * Convierte una fecha de GIS a un Date a medianoche local de ese mismo día,
 * que es como DxDateBox compara min, max y disabledDates.
 * @param {String} date
 * @return {Date}
 */
export function toLocalDay(date) {
  return moment(toDayKey(date), DAY_FORMAT).toDate();
}

/**
 * Devuelve los días (medianoche local) entre la primera y la última fecha
 * disponible que no tienen dato, para deshabilitarlos en el datepicker.
 * Las fechas ya excluidas se tratan como disponibles para que sigan siendo
 * seleccionables aunque GIS ya no las devuelva.
 * @param {Array<String>} availableDates Fechas disponibles, ordenadas
 * @param {Array<String>} excludedDates Fechas excluidas 'YYYY-MM-DD'
 * @return {Array<Date>}
 */
export function buildDisabledDates(availableDates = [], excludedDates = []) {
  if (availableDates.length === 0) {
    return [];
  }
  const enabledDays = new Set([...availableDates, ...excludedDates].map(toDayKey));
  const disabledDates = [];
  const current = moment(toDayKey(availableDates[0]), DAY_FORMAT);
  const end = moment(toDayKey(availableDates[availableDates.length - 1]), DAY_FORMAT);
  while (current.isSameOrBefore(end, 'day')) {
    if (!enabledDays.has(current.format(DAY_FORMAT))) {
      disabledDates.push(current.toDate());
    }
    current.add(1, 'day');
  }
  return disabledDates;
}

/**
 * Rango 'YYYY-MM-DD' con el que se pide la línea temporal: el seleccionado en el
 * store (por defecto el último mes) o, si no hay, hoy.
 * @param {String} initIntervalDate
 * @param {String} endIntervalDate
 * @return {{initDate: String, endDate: String}}
 */
export function getTimelineRange(initIntervalDate, endIntervalDate) {
  const today = moment().format(DAY_FORMAT);
  return {
    initDate: initIntervalDate ? moment(initIntervalDate).format(DAY_FORMAT) : today,
    endDate: endIntervalDate ? moment(endIntervalDate).format(DAY_FORMAT) : today,
  };
}
